import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Shield, Loader2, CheckCircle, AlertTriangle, RefreshCw } from "lucide-react";
import axios from "axios";

const API_URL = window.location.origin;

export default function SetupAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "Pablo Lopez",
    email: "gerencia@tenatourgdl.com",
    password: "Emer$2026"
  });

  // Check system status on mount
  useEffect(() => {
    checkSystemStatus();
  }, []);

  const checkSystemStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/system-status`);
      setSystemStatus(response.data);
      
      // If admin exists, show message
      if (response.data.admin_exists) {
        toast.info("Ya existe un administrador en el sistema. Use el login normal.");
      }
    } catch (error) {
      console.error("Error checking system status:", error);
      setSystemStatus({ status: "error", error: error.message });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/bootstrap-admin`, formData);
      
      if (response.data.user) {
        setSuccess(true);
        toast.success("¡Super Admin creado exitosamente!");
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (error) {
      console.error("Bootstrap error:", error);
      console.error("Response data:", error.response?.data);
      console.error("Status:", error.response?.status);
      
      let message = "Error al crear el administrador";
      
      if (error.response?.data?.detail) {
        // Handle both string and array detail formats
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          message = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        } else {
          message = JSON.stringify(detail);
        }
      } else if (error.message) {
        message = error.message;
      }
      
      toast.error(message);
      
      // If admin already exists, show specific message and redirect
      if (message.includes("Ya existe")) {
        toast.info("Redirigiendo al login...");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="text-2xl font-bold text-green-700">¡Configuración Completada!</h2>
              <p className="text-slate-600">
                Super Admin creado exitosamente.<br />
                Redirigiendo al login...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Configuración Inicial</CardTitle>
          <CardDescription>
            Crear el primer Super Administrador del sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* System Status Panel */}
          <div className="mb-4 p-3 bg-slate-100 rounded-lg text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">Estado del Sistema</span>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                onClick={checkSystemStatus}
                disabled={statusLoading}
              >
                <RefreshCw className={`w-4 h-4 ${statusLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {statusLoading ? (
              <p className="text-slate-500">Verificando...</p>
            ) : systemStatus ? (
              <div className="space-y-1 text-xs">
                <p>Base de datos: {systemStatus.database_connected ? '✅ Conectada' : '❌ Error'}</p>
                <p>Administradores: {systemStatus.admin_count || 0}</p>
                <p>Total usuarios: {systemStatus.total_users || 0}</p>
                {systemStatus.admin_exists && (
                  <div className="mt-2 p-2 bg-amber-100 rounded text-amber-800">
                    <strong>Ya existe un admin:</strong>
                    {systemStatus.admins?.map((a, i) => (
                      <div key={i}>{a.email} ({a.is_active ? 'Activo' : 'Inactivo'})</div>
                    ))}
                  </div>
                )}
                {systemStatus.can_bootstrap && (
                  <p className="text-green-600 font-medium">✅ Puede crear el primer admin</p>
                )}
                {systemStatus.error && (
                  <p className="text-red-600">Error: {systemStatus.error}</p>
                )}
              </div>
            ) : (
              <p className="text-red-500">No se pudo verificar el estado</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del administrador"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@ejemplo.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Contraseña segura"
                required
                minLength={6}
              />
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Este formulario solo funciona si no existe ningún administrador en el sistema.
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Crear Super Admin
                </>
              )}
            </Button>
            
            <div className="text-center">
              <Button 
                type="button" 
                variant="link" 
                onClick={() => navigate("/login")}
                className="text-sm"
              >
                Ya tengo cuenta, ir al Login
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
